import React, { useState, useEffect } from "react";
import {
  Box,
  Stack,
  Typography,
  Button,
  CircularProgress,
  TextField,
} from "@mui/material";
import { UploadOutlined } from "@ant-design/icons";
import toast from "react-hot-toast";
import axios from "axios";
import { URL } from "../config";

const CameraBrandsModal = ({ onClose, brand }) => {
  const [cameraBrandName, setCameraBrandName] = useState(brand?.cameraBrandName || "");
  const [imageFile, setImageFile] = useState(null);
  const [preview, setPreview] = useState(brand?.cameraBrandImage ? `${URL}${brand.cameraBrandImage}` : null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setCameraBrandName(brand?.cameraBrandName || "");
    setPreview(brand?.cameraBrandImage ? `${URL}${brand.cameraBrandImage}` : null);
    setImageFile(null);
  }, [brand]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file?.type.startsWith("image/")) {
      toast.error("Only image files allowed!");
      return;
    }
    setImageFile(file);
    setPreview(window.URL.createObjectURL(file));
  };

  const handleSubmit = async () => {
    if (!cameraBrandName.trim()) {
      toast.error("Please enter brand name!");
      return;
    }

    if (!imageFile && !brand?.cameraBrandImage) {
      toast.error("Please select a brand image!");
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("cameraBrandName", cameraBrandName);
      if (imageFile) formData.append("cameraBrandImage", imageFile);

      if (brand) {
        await axios.patch(`${URL}/camerabrands/${brand.cameraBrandId}`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Brand updated successfully!");
      } else {
        await axios.post(`${URL}/camerabrands`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Brand added successfully!");
      }

      setCameraBrandName("");
      setImageFile(null);
      setPreview(null);
      setTimeout(() => onClose?.(), 500);
    } catch (err) {
      console.error(err);
      toast.error("Upload failed!");
    } finally {
      setLoading(false);
    }
  };

  const fieldStyle = {
    "& .MuiOutlinedInput-root": {
      minHeight: "28px",
      fontSize: "14px",
      "& fieldset": { borderColor: "#c4c4c4" },
      "&:hover fieldset": { borderColor: "#184459ff" },
      "&.Mui-focused fieldset": { borderColor: "#c4c4c4", borderWidth: "1.5px" },
    },
    "& .MuiInputLabel-root": { fontSize: "18px", color: "#184459ff", fontWeight: 600 },
    "& .MuiInputLabel-root.Mui-focused": { color: "#184459ff" },
  };

  return (
    <Box sx={{
      position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh",
      bgcolor: "rgba(0,0,0,0.6)", display: "flex", justifyContent: "center",
      alignItems: "center", zIndex: 1300, p: 2
    }}>
      <Box sx={{ width: { xs: "90%", sm: 400 }, bgcolor: "#fff", borderRadius: 2, p: 3 }}>
        <Typography variant="h6" sx={{ textAlign: "center", color: "#184459ff", mb: 2 }}>
          {brand ? "Edit Brand" : "Add Brand"}
        </Typography>

        <Stack spacing={2}>
          <TextField
            fullWidth
            label="Brand Name"
            variant="outlined"
            size="small"
            value={cameraBrandName}
            onChange={(e) => setCameraBrandName(e.target.value)}
            sx={fieldStyle}
            InputLabelProps={{ shrink: true }}
          />

          <Box sx={{ display: "flex", alignItems: "center", gap: 2, p: 1.5, borderRadius: 2, border: "1px solid #c4c4c4", bgcolor: "#f9f9f9" }}>
            <Button component="label" startIcon={<UploadOutlined />} sx={{ textTransform: "none", borderRadius: 2, py: 0.7, px: 1.5, fontSize: "0.85rem" }}>
              Select Image
              <input type="file" hidden accept="image/*" onChange={handleImageChange} />
            </Button>
            {preview && <Box sx={{ width: 50, height: 50, borderRadius: 1.5, overflow: "hidden", border: "1px solid #c4c4c4" }}>
              <img src={preview} alt="preview" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            </Box>}
          </Box>

          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button
              variant="contained"
              sx={{ backgroundColor: "#184459ff", "&:hover": { backgroundColor: "#184459ff" } }}
              onClick={handleSubmit}
              startIcon={loading && <CircularProgress size={18} />}
            >
              {loading ? "Uploading..." : brand ? "Update" : "Upload"}
            </Button>

            <Button
              variant="contained"
              sx={{ backgroundColor: "#757575ff", "&:hover": { backgroundColor: "#757575ff" } }}
              onClick={onClose}
            >
              Close
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
};

export default CameraBrandsModal;
